import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import _ from "lodash";

//components
import AppHOC from "../../../../generics/AppHOC";
import Spinner from "../../../../common/Spinner";
import ErrorComponent from "../../../../common/ErrorComponent";
import PathRecommendation from "../../../../common/PathRecommendation";
import GeoMapComponentMini, {
  getValidCoordinates,
} from "./GeoMapComponentMini";

//utils
import {
  getLocalStorageFlag,
  isDeviceInfoPage,
} from "../../../../utils/common";
import usePredictiveNetworksHooks from "../../../insights/usePredictiveNetworkHooks";
import { validateProps } from "../../../../utils/waniCommon";
import { routeURLs } from "../../../../utils/enums";

//style
import css from "../../insightsAppMagneticStyle.less";

import useI18n from "../../../../i18n/useI18n";

const mapStateToProps = (state) => {
  return {
    predictiveNetworks: state.vanalytics.predictiveNetworks,
  };
};

const [HbrCard] = reactWrapper(["hbr-card"]);

const ActionCards = (props) => {
  const { tViews } = useI18n("views");
  const navigate = useNavigate();
  const { selectedOverlay, selectedSite, appGroup, onboardingStatus } =
    validateProps(props.globalFilter);
  const [isLoading, setIsLoading] = useState(true);
  const [cards, setCards] = useState(<></>);
  const { state, getListActions, getEndpointsDetails, getPathRecommendation } =
    usePredictiveNetworksHooks(props);
  const pathDetails = JSON.parse(getLocalStorageFlag("pathDetails"));

  useEffect(() => {
    if (isDeviceInfoPage()) getPathRecommendation();
  }, []);

  useEffect(() => {
    const runEffect = async () => {
      if (onboardingStatus === "ACTIVE") {
        window.scrollTo(0, 0);

        const actionCardSite = getLocalStorageFlag("actionCardSite");

        if (!isLoading) {
          setIsLoading(true);
        }

        if (_.isEmpty(pathDetails) && isDeviceInfoPage())
          navigate(routeURLs.pn);

        if (parseInt(actionCardSite) !== -1) {
          getListActions(selectedOverlay);
          getEndpointsDetails(selectedOverlay, selectedSite);
        } else if (selectedSite === -1) {
          navigate(routeURLs.pn);
        }
      } else {
        setIsLoading(false);
      }
    };

    runEffect();
  }, [appGroup]);

  useEffect(() => {
    let cards = null;

    if (state.isFetchingActionsDone) {
      const actionCardSite = getLocalStorageFlag("actionCardSite");
      const filteredActionBySite = state.actionPaths.filter(
        (action) =>
          action.siteId === actionCardSite.toString() &&
          action.appClass === appGroup,
      );

      if (!_.isEmpty(filteredActionBySite)) {
        cards = filteredActionBySite.map((card) => {
          const coordinates = getValidCoordinates(card.location);

          return (
            <div className="card-action-view" key={Math.random()}>
              {coordinates && (
                <div
                  className="card-action-view-mini-map-container"
                  data-cy="card-action-view-mini-map-container"
                >
                  <GeoMapComponentMini
                    longitude={coordinates.longitude}
                    latitude={coordinates.latitude}
                  />
                </div>
              )}
              <div className="card-action-view-group">
                <div className="card-action-view-row-1 hbr-type-body3">
                  <span>
                    {tViews("predictiveNetworks.appTableConfigSiteIdLabel")}
                  </span>
                </div>
                <div className="card-action-view-row-2 hbr-type-body3">
                  {filteredActionBySite[0].siteId}
                </div>
                <div className="card-action-view-row-1 hbr-type-body3">
                  <span>
                    {tViews("predictiveNetworks.appTableConfigLocationLabel")}
                  </span>
                </div>
                <div className="card-action-view-row-2 hbr-type-body3">
                  {`${filteredActionBySite[0].location.country} · ${filteredActionBySite[0].location.city}`}
                </div>
              </div>
              {pathDetails ? (
                <>
                  <div className="card-action-view-group">
                    <div className="card-action-view-row-3 card-action-view-recommendation-label hbr-type-body3">
                      {tViews("predictiveNetworks.recommendation")}
                    </div>
                    <div className="card-action-view-row-2 hbr-type-body3">
                      {_.isArray(state.pathRecommendation) &&
                        state.pathRecommendation[0]?.metadata && (
                          <PathRecommendation
                            {...state.pathRecommendation[0].metadata}
                            isTrimmed
                          />
                        )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="card-action-view-group">
                    <div className="card-action-view-row-3 card-action-view-recommendation-label hbr-type-body3">
                      <span>{tViews("predictiveNetworks.recommendation")}</span>
                    </div>
                    <div className="card-action-view-row-2 hbr-type-body3">
                      <PathRecommendation {...card.metadata} isTrimmed />
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        });
      } else {
        navigate(routeURLs.pn);
      }
      setCards(cards);
      setIsLoading(false);
    }
  }, [
    state.isFetchingActionsDone,
    state.isFetchingActionsError,
    state.isFetchingRecommedationsDone,
    state.isFetchingRecommedationsError,
  ]);

  if (isLoading) {
    return (
      <div className={css["card-action"]} data-cy="card-action">
        <div className="spinner-card-action" data-cy="spinner-card-action">
          <Spinner />
        </div>
      </div>
    );
  } else if (state.isFetchingActionsError?.errorObject) {
    return (
      <div className={css["card-action"]} data-cy="card-action">
        <ErrorComponent
          {...state.isFetchingActionCardsError.errorObject}
          width={"200px"}
          className={"super-small-dashlet-error"}
        />
      </div>
    );
  } else {
    return (
      <HbrCard className={css["card-action"]} data-cy="card-action">
        {cards}
      </HbrCard>
    );
  }
};
ActionCards.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  getActionCardsPerSite: PropTypes.func,
};

export default connect(mapStateToProps)(AppHOC(ActionCards));
